/**
 * Modern Order Details Page - Version 2
 * 
 * Complete redesign with Shopify/AfterShip-style UI:
 * - Header bar with Order ID, progress bar, actions
 * - Hero strip with photo carousel and quick facts
 * - Two-column body with order items, measurements, timeline, photos
 * - Status update modals and inline editing
 * - Fully responsive design
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrderDetails } from '../../hooks/useOrderDetails';
import { CardSkeleton } from '../shared/SkeletonLoader';
import { EnhancedHeader } from '../common/EnhancedHeader';
import OrderHeader from './OrderHeader';
import OrderHero from './OrderHero';
import OrderItemsCard from './OrderItemsCard';
import MeasurementsCard from './MeasurementsCard';
import ReceiptsCard from './ReceiptsCard';
import TimelineCard from './TimelineCard';
import PhotosCard from './PhotosCard';
import NotesCard from './NotesCard';
import ActivityCard from './ActivityCard';
import StatusUpdateModal from './StatusUpdateModal';
import PhotoUploadModal from './PhotoUploadModal';
import EditOrderModal from './EditOrderModal';
import InvoicePrintView from './InvoicePrintView';
import MeasurementSetSelector from './MeasurementSetSelector';
import DeleteOrderConfirmationModal from './DeleteOrderConfirmationModal';
import { StatusUpdateRequest, OrderStatus, PhotoTag, TimelineProgress } from '../../types/order';
import { API_BASE } from '../../config/api';

const OrderDetailsV2: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { order, loading, error, mutate } = useOrderDetails({ 
    orderNumber: orderNumber || '' 
  });
  
  // Timeline progress state
  const [timelineProgress, setTimelineProgress] = useState<TimelineProgress | null>(null);
  
  // Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [photoUploadModalOpen, setPhotoUploadModalOpen] = useState(false);
  const [measurementSelectorOpen, setMeasurementSelectorOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [allowedNextStatuses, setAllowedNextStatuses] = useState<OrderStatus[]>([]);
  
  // Fetch timeline progress separately
  useEffect(() => {
    const fetchTimelineProgress = async () => {
      if (!orderNumber || !user) return;
      
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        
        const response = await fetch(`${API_BASE}/api/orders/${orderNumber}/timeline`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.progress) {
            setTimelineProgress(data.progress);
          }
        }
      } catch (_err) {
        console.error('Failed to fetch timeline progress:', err);
      }
    };
    
    if (order) {
      fetchTimelineProgress();
    }
  }, [order, orderNumber, user]);
  
  const getDashboardRoute = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'tailor':
        return '/business/dashboard';
      case 'customer':
        return '/customer/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };

  // Check if user can delete the order
  // Backend will enforce permissions, but we can show/hide the button for better UX
  const canDeleteOrder = (): boolean => {
    if (!user || !order) return false;
    
    // Admins can always delete
    if (user.role === 'admin') return true;
    
    // Tailors can delete orders assigned to them
    if (user.role === 'tailor' && order.assignedTailor?.id === user.id) {
      return true;
    }
    
    // Customers can delete their own orders
    // Note: We can't check customer.user_id from the DTO, so we'll show the button
    // and let the backend handle the permission check
    if (user.role === 'customer') {
      return true;
    }
    
    return false;
  };
  
  const handleAction = (action: string) => {
    if (action === 'update-status') {
      // Get allowed next statuses from backend or use default transitions
      if (order) {
        // For now, use client-side logic - will be enhanced with backend call
        const transitions: Record<OrderStatus, OrderStatus[]> = {
          CREATED: ['IN_PROGRESS', 'CANCELLED', 'ON_HOLD'],
          IN_PROGRESS: ['PACKED', 'READY', 'ON_HOLD', 'CANCELLED'],
          PACKED: ['READY', 'IN_PROGRESS', 'ON_HOLD', 'CANCELLED'],
          READY: ['COMPLETED', 'PACKED', 'ON_HOLD', 'CANCELLED'],
          ON_HOLD: ['CREATED', 'IN_PROGRESS', 'PACKED', 'READY', 'CANCELLED'],
          COMPLETED: [],
          CANCELLED: [],
        };
        setAllowedNextStatuses(transitions[order.status] || []);
        setStatusModalOpen(true);
      }
    } else if (action === 'more') {
      // More menu is handled by MoreActionsMenu component
    }
  };
  
  const handleAddPrice = () => {
    // TODO: Open price editor modal
    console.log('Add price');
  };
  
  const handleAttachMeasurement = () => {
    if (order?.customer?.id) {
      setMeasurementSelectorOpen(true);
    }
  };
  
  const handleSwitchMeasurement = () => {
    if (order?.customer?.id) {
      setMeasurementSelectorOpen(true);
    }
  };
  
  const handleAttachMeasurementSet = async (measurementSetId: string) => {
    // Validate measurementSetId before sending
    if (!measurementSetId || measurementSetId === 'null' || measurementSetId === 'undefined') {
      throw new Error('Invalid measurement set ID');
    }
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/orders/${orderNumber}/attach-measurement-set`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ measurementSetId }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to attach measurement set');
    }
    
    // Refresh order data
    await mutate();
  };
  
  const handleMoreActions = {
    edit: () => {
      setEditModalOpen(true);
    },
    print: () => {
      setShowPrintView(true);
      // Use setTimeout to ensure DOM is updated before printing
      setTimeout(() => {
        window.print();
        // Hide print view after print dialog closes
        setTimeout(() => setShowPrintView(false), 500);
      }, 100);
    },
    share: () => {
      const url = window.location.href;
      if (navigator.share) {
        navigator.share({ title: `Order ${order?.id}`, url });
      } else {
        navigator.clipboard.writeText(url);
        alert('Tracking link copied to clipboard!');
      }
    },
    download: () => {
      // TODO: Implement download order details as PDF
      console.log('Download order details');
    },
    delete: () => {
      setDeleteError(null);
      setDeleteModalOpen(true);
    },
  };

  const handleDeleteOrder = async () => {
    if (!order || !orderNumber) {
      setDeleteError('Order information is missing');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      const response = await fetch(`${API_BASE}/api/orders/${orderNumber}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || 'Failed to delete order';
        
        // Handle permission errors specifically
        if (response.status === 403) {
          throw new Error('You do not have permission to delete this order. Only admins and order owners can delete orders.');
        }
        
        if (response.status === 404) {
          throw new Error('Order not found. It may have already been deleted.');
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Close modal and navigate to dashboard
      setDeleteModalOpen(false);
      
      // Show success message briefly before navigating
      // In a real app, you might want to show a toast notification
      setTimeout(() => {
        navigate(getDashboardRoute());
      }, 500);
    } catch (_error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete order. Please try again.');
      console.error('Error deleting order:', error);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleUploadPhoto = () => {
    setPhotoUploadModalOpen(true);
  };
  
  const handleStatusUpdate = async (request: StatusUpdateRequest) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    
    const response = await fetch(`${API_BASE}/api/orders/${orderNumber}/status`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail?.error || 'Failed to update status');
    }
    
    // Refresh order data
    await mutate();
  };
  
  const handlePhotoUpload = async (file: File, tag: PhotoTag) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    
    // Convert tag to lowercase for backend (FRONT -> front)
    const tagLower = tag.toLowerCase();
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tag', tagLower);
    
    const response = await fetch(`${API_BASE}/api/orders/${orderNumber}/photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type - browser will set it with boundary for multipart
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(error.detail || 'Failed to upload photo');
    }
    
    const result = await response.json();
    console.log('[DEBUG] Photo upload response:', result);
    
    // Refresh order data - wait a moment for backend to commit
    await new Promise(resolve => setTimeout(resolve, 500));
    await mutate();
  };
  
  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) {
      return;
    }
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/orders/${orderNumber}/photos/${photoId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete photo');
    }
    
    // Refresh order data
    await mutate();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          <CardSkeleton />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <CardSkeleton />
              <CardSkeleton />
            </div>
            <div className="space-y-6">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-800 font-semibold text-lg mb-2">Error Loading Order</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="flex gap-3">
              <button
                onClick={() => mutate()}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
              <button
                onClick={() => navigate(getDashboardRoute())}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Order Not Found</h2>
            <p className="text-gray-600 mb-4">Order {orderNumber} could not be found.</p>
            <button
              onClick={() => navigate(getDashboardRoute())}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Platform Header */}
      <EnhancedHeader
        title={`${user?.name || 'User'}'s Master Ji`}
        showBackButton={true}
        backTo={getDashboardRoute()}
        userTier={user?.tier}
      />
      
      {/* Order Header with Progress Bar - Matched width with content */}
      <div className="max-w-7xl mx-auto px-4">
        <OrderHeader 
          order={order} 
          onAction={handleAction}
          onMoreActions={{
            ...handleMoreActions,
            delete: canDeleteOrder() ? handleMoreActions.delete : undefined,
          }}
        />
      </div>
      
      {/* Hero Strip */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <OrderHero order={order} onAddPrice={handleAddPrice} />
      </div>
      
      {/* Two-Column Body - Responsive */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12">
        {/* Mobile: Single column, Tablet: Stack with sticky actions, Desktop: Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            <OrderItemsCard order={order} />
            <MeasurementsCard
              order={order}
              onAttachMeasurement={handleAttachMeasurement}
              onSwitchMeasurement={handleSwitchMeasurement}
            />
            <ReceiptsCard
              order={order}
              onReceiptCreated={async () => {
                // Refetch order details to get updated receipts
                await mutate();
              }}
            />
            <NotesCard order={order} />
          </div>
          
          {/* Right Column */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <TimelineCard order={order} progress={timelineProgress || undefined} />
            <PhotosCard
              order={order}
              onUploadPhoto={handleUploadPhoto}
              onDeletePhoto={handleDeletePhoto}
            />
            <ActivityCard order={order} />
          </div>
        </div>
      </div>
      
      {/* Sticky Update Status Button on Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-40">
        <button
          onClick={() => handleAction('update-status')}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
        >
          Update Status
        </button>
      </div>
      
      {/* Modals */}
      {order && (
        <>
          <StatusUpdateModal
            isOpen={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
            currentStatus={order.status}
            allowedNextStatuses={allowedNextStatuses}
            onSubmit={handleStatusUpdate}
          />
          <PhotoUploadModal
            isOpen={photoUploadModalOpen}
            onClose={() => setPhotoUploadModalOpen(false)}
            onUpload={(file, tag) => handlePhotoUpload(file, tag)}
          />
          <EditOrderModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            order={order}
            onUpdate={mutate}
          />
          <MeasurementSetSelector
            isOpen={measurementSelectorOpen}
            onClose={() => setMeasurementSelectorOpen(false)}
            customerId={order.customer.id}
            onSelect={handleAttachMeasurementSet}
            currentMeasurementSetId={order.measurementSet?.id}
          />
          <DeleteOrderConfirmationModal
            isOpen={deleteModalOpen}
            onClose={() => {
              setDeleteModalOpen(false);
              setDeleteError(null);
            }}
            onConfirm={handleDeleteOrder}
            orderId={order.id}
            isDeleting={isDeleting}
            error={deleteError}
          />
        </>
      )}

      {/* Print View */}
      {showPrintView && order && (
        <div className="print-container-parent fixed inset-0 bg-white z-50 overflow-auto">
          <InvoicePrintView order={order} />
        </div>
      )}
    </div>
  );
};

export default OrderDetailsV2;

